'use client';

import { GrowthBook, type GrowthBookPayload, GrowthBookProvider } from '@growthbook/growthbook-react';
import { useConsentStore } from '@utils/utils-analytics';
import { initTracking } from '@utils/utils-feature-flags/init-tracking';
import { type PropsWithChildren, useEffect, useRef } from 'react';
import { clientEnv } from '@/env/client';
type Props = PropsWithChildren<{
  payload: GrowthBookPayload;
  userId: string;
}>;
const trackedResults = new Set<string>();
export function GrowthBookAppProvider({
  children,
  payload,
  userId
}: Props) {
  const hasGrowthBookConsent = useConsentStore(store => Boolean(store.details?.GrowthBook));

  // The tracking callback is set once and captures consent via a ref so it can
  // respect a later revocation without freezing the initial value in its closure.
  const consentRef = useRef(hasGrowthBookConsent);
  useEffect(() => {
    consentRef.current = hasGrowthBookConsent;
  });

  // One GrowthBook instance per component lifetime.
  // Rebuilding it (e.g. on payload/userId changes) would discard in-memory state like
  // `setForcedFeatures` — which is exactly what the GrowthBook DevTools extension uses
  // to override flag values. useRef + null-check also avoids double construction under
  // React strict mode (unlike useState, whose initializer runs twice).
  const growthBookRef = useRef<GrowthBook | null>(null);

  /* oxlint-disable react/refs -- lazy one-time init, see comment above */
  if (!growthBookRef.current) {
    growthBookRef.current = new GrowthBook({
      apiHost: clientEnv.NEXT_PUBLIC_GROWTHBOOK_API_HOST,
      clientKey: clientEnv.NEXT_PUBLIC_GROWTHBOOK_CLIENT_KEY,
      decryptionKey: clientEnv.NEXT_PUBLIC_GROWTHBOOK_DECRYPTION_KEY,
      attributes: {
        id: userId
      },
      enableDevMode: clientEnv.NEXT_PUBLIC_APP_ENV !== 'production'
      // No trackingCallback at construction — on purpose.
      // GrowthBook invokes the tracking callback at most once per experiment and marks
      // the experiment as tracked *before* calling it. An experiment evaluated on first
      // paint — e.g. an EDP opened in a fresh tab, before Usercentrics has hydrated the
      // consent store — would fire that single callback while consent is still unknown.
      // With a callback that bails out on missing consent the event is lost forever.
      // While no callback is registered GrowthBook instead buffers the calls (deferred
      // tracking); we register the real callback once consent is present (see below),
      // which flushes the buffer via fireDeferredTrackingCalls().
    });
    growthBookRef.current.initSync({
      payload
    });
  }
  const growthBook = growthBookRef.current;
  /* oxlint-enable react/refs */

  // Register the tracking callback only once GrowthBook consent is granted. Doing so
  // fires any buffered (deferred) calls, recovering experiments that were evaluated
  // before consent was hydrated. Registering just once keeps the buffered calls intact;
  // the in-callback consent check then handles a later revocation.
  const trackingRegisteredRef = useRef(false);
  useEffect(() => {
    if (!hasGrowthBookConsent || trackingRegisteredRef.current) return;
    trackingRegisteredRef.current = true;
    growthBook.setTrackingCallback((event, result) => {
      if (!consentRef.current) return;
      const dedupKey = `${event.key}:${result.hashValue}`;
      if (trackedResults.has(dedupKey)) return;
      trackedResults.add(dedupKey);
      void initTracking(event, result);
    });
  }, [growthBook, hasGrowthBookConsent]);

  // Skip the initial effect fire: payload/userId on first render are the same values
  // already passed into the constructor. Calling setPayload/setAttributes again right
  // after init would trigger a redundant re-render and can race with the DevTools
  // extension's override attempt.
  const lastPayloadRef = useRef(payload);
  const lastUserIdRef = useRef(userId);
  useEffect(() => {
    if (lastPayloadRef.current !== payload) {
      lastPayloadRef.current = payload;
      void growthBook.setPayload(payload);
    }
  }, [growthBook, payload]);
  useEffect(() => {
    if (lastUserIdRef.current !== userId) {
      lastUserIdRef.current = userId;
      void growthBook.setAttributes({
        id: userId
      });
    }
  }, [growthBook, userId]);
  return <GrowthBookProvider growthbook={growthBook}>{children}</GrowthBookProvider>;
}